/**
 * @module util/helpers
 * @description General helper functions
 *
 * @example
 * import helper from './util/helper';
 *
 *
 * // In your files you can import ALL the helpers like this
 * import * as someHelper from './util/helper';
 *
 * // and use like this
 * someHelper.doSomething();
 *
 * // OR
 *
 * //Import them individually
 *
 * // and use like this
 * import { doSomething } from './util/helper';
 * doSomething();
 *
 */
import qs from "query-string";

/**
 * Utility function to analyzePdpUrlPath
 * @function
 */
export const analyzePdpUrlPath = (url) => {
  if (!url) return {};
  const urlParts = url.split("/");
  const category = urlParts[2];
  const productId = urlParts[3];
  return {
    category,
    productId
  };
};

/**
 * convert decimal to dollar format
 * // returns $89.00
 * convertDecimalToDollars(89.000000);
 * @returns {String} Returns the the number formatted in dollars.
 */
export const convertDecimalToDollars = (priceInDecimal) =>
  `$${parseFloat(priceInDecimal).toFixed(2)}`;

export const getFeatureCDP = (productPayload = {}) => {
  let { "product-whats-new": isNewProduct, "display-name": displayName } =
    productPayload;
  // Out of stock - This scenario is specific to the Feature Tile
  if (productPayload.id) {
    displayName = productPayload.name;
  }
  const onlineOnly =
    (displayName || "").toLowerCase().includes("online only") && "online only";
  const newProduct = isNewProduct === "1" && "new";
  return [onlineOnly, newProduct].filter(Boolean);
};

/**
 * Utility function that hashes a stringified object containing some unique value
 * and returns a hashed numeric value.
 * Performs @ ~900k ops/sec
 * @param {string} stringified numeric hash
 */
const generateHash = (str) => {
  let hash = 0;
  const len = str.length;

  if (len === 0) {
    return hash;
  }

  for (let i = 0; i < len; i += 1) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0; // Convert to 32bit integer
  }

  return hash;
};

const stringify = (obj) => JSON.stringify(obj);

/**
 * Utility function that stringifies an object containing some unique value
 * and returns a hashed stringified value
 * @param {object}
 */
export const createEncodedKey = (strOrObj) =>
  stringify(generateHash(stringify(strOrObj)));

/**
 * @param  {...string|Object} args
 * @return {string}
 */
export const mergeQueryParams = (...args) => {
  let merged = {};
  args.forEach((arg) => {
    if (typeof arg === "undefined") {
      // ignore
    } else if (typeof arg === "string") {
      const parsed = qs.parse(arg);
      merged = { ...merged, ...parsed };
    } else if (typeof arg === "object" && !Array.isArray(arg)) {
      merged = { ...merged, ...arg };
    } else {
      throw new Error("Invalid argument");
    }
  });

  return Object.keys(merged).length ? `?${qs.stringify(merged)}` : "";
};

/**
 * @description Strip white spaces from a string.
 * @param {string}
 * @returns {string}
 */
export const stripWhitespace = (string = "") => {
  let processedString = string;
  if (typeof string === "string") {
    processedString = string.replace(/\s/g, "");
  }
  return processedString;
};

/**
 * @description Mimics CSS `text-transform: capitalize`.
 * @param {string}
 * @returns {string}
 */
export function capitalize(text = "") {
  if (typeof text === "string") {
    return text.toLowerCase().replace(/\b\w/g, (m) => m.toUpperCase());
  }

  return text;
}

/**
 * @description Adjusts pixels to their rem-adjusted value (useful when using `rem` sass function)
 * @param {number}
 * @returns {number}
 */
export function remPx(basePixels = 0) {
  const userBase = parseFloat(
    getComputedStyle(document.documentElement).fontSize
  );

  return (userBase / 16) * basePixels;
}

/**
 * @description Wrapper of setTimeout to async/await on callback function within setTimeout
 * @param {object}
 * @param {number}
 */
export const setAsyncTimeout = (cb, timeout = 0) =>
  new Promise((resolve) => {
    setTimeout(() => {
      cb();
      resolve();
    }, timeout);
  });

/**
 * Convert to Kiwi-accepted locale
 * @param {string} locale
 * @returns {string} formatted locale: xx-xx
 */
export const convertLocaleForGraphql = (locale) => {
  return locale?.replace("_", "-").toLowerCase();
};
